import { capitalize, markRaw } from 'vue';
import { Store } from 'vuex';

import allPollens from '@/common/components/controls/targeting/pollen/PollenTargetingEnumerations';
import allUVs from '@/common/components/controls/targeting/uv/UVTargetingEnumerations';
import * as WeatherTargetingEnumerations from '@/common/components/controls/targeting/weather/WeatherTargetingEnumerations';
import { allWeathers } from '@/common/components/controls/targeting/weather/WeatherTargetingEnumerations';
import { Criterion, CriterionValue } from '@/common/components/entity-form/EntityType';
import PurchaseTypes from '@/common/enums/purchase-types.enum';
import TargetingCriteriaType, { isWeatherCriteria } from '@/common/enums/TargetingCriteriaType.enum';
import {
  IconDaypart,
  IconEvent,
  IconFees,
  IconLocation,
  IconMediaOwner,
  IconPollen,
  IconWeather,
} from '@/common/icons/monochrome';
import { Creative, LineItem, LineItemTargetingCriterion, Package, Plan, TargetingCriterion } from '@/common/models';
import MediaOwners from '@/common/models/media-owner/MediaOwners';
import PackageTargetingCriterion from '@/common/models/package/PackageTargetingCriterion';
import PlanTargetingCriterion from '@/common/models/plan/types/PlanTargetingCriterion';
import TargetingCriterionSubType from '@/common/models/targeting-criteria/TargetingCriterionSubType';
import { celsiusToFahrenheit, FullDaypart, getStore, region, Regions } from '@/globals';
import {
  CustomEvents as CreativeCustomEvents,
  Daypart as CreativeDaypart,
  Locations,
  Pollen as CreativePollen,
  Weather as CreativeWeather,
} from '@/org-app/components/creatives/targeting';
import {
  CustomEvents as CustomEventsSection,
  Daypart,
  ExchangeFeeOverride as ExchangeFeeOverrideSection,
  LineItemMediaOwners,
  Pollen as PollenSection,
  Weather,
} from '@/org-app/components/lineItems/targeting';
import PlanMediaOwners from '@/org-app/components/lineItems/targeting/MediaOwners/PlanMediaOwners.vue';
import { useTargetingCriteria } from '@/org-app/services/tanstack/queries';
import { peekQueryInScope, runQueryInScope } from '@/org-app/services/tanstack/queryUtils';
import LocalCache from '@/utils/LocalCache';
import i18n from '@/vueI18n';

export interface ITargetingCriteria {
  type: TargetingCriteriaType;
  isUpdatePushedExternally: boolean;
  id: number;
  subTypes?: TargetingCriterionSubType[];
}

export interface ISection {
  id: TargetingSection;
  name: string;
  component: unknown;
  icon: unknown;
  featureKey?: string | { key: string; transient: boolean };
}

export enum TargetingCode {
  Location = 'LOCATION',
  Weather = 'WEATHER',
  PollenCategory = 'POLLEN_CATEGORY',
  RainMmLast12hAbove = 'RAIN_MM_LAST_12H_ABOVE',
  SnowCmLast12hAbove = 'SNOW_CM_LAST_12H_ABOVE',
}

export enum TargetingSection {
  CustomEvents = 'custom-events',
  Daypart = 'daypart',
  ExchangeFeeOverride = 'exchange_fee_override',
  Inventory = 'locations',
  MediaOwners = 'mediaowners',
  Pollen = 'pollen',
  Uv = 'uv',
  Weather = 'weather',
}

export default class TargetingSectionHelper {
  entity: LineItem | Package | Plan | Creative;
  store: Store<{
    orgApp: {
      ReferenceData: {
        targetingCriteria: TargetingCriterion[];
        organizationSettings: {
          allowExchangeFeeOverride: boolean;
          uiEnableCustomEventManagement: boolean;
          enableJourneys: boolean;
        };
      };
      organization: { isDemandOrganization: boolean; isCuratorOrganization: boolean };
      organizationSettings: { allowExchangeFeeOverride: boolean; uiEnableCustomEventManagement: boolean };
    };
  }>;
  isPublic: boolean;

  constructor(entity: LineItem | Plan | Package | Creative, isPublic?: boolean) {
    this.store = getStore();
    this.entity = entity;
    this.isPublic = isPublic;
  }

  get state() {
    return this.store?.state;
  }

  get isDemandOrganization(): boolean {
    return this.state?.orgApp?.organization?.isDemandOrganization || false;
  }

  get isCuratorOrganization(): boolean {
    return this.state?.orgApp?.organization?.isCuratorOrganization || false;
  }

  static get isNotChina(): boolean {
    return region !== Regions.China;
  }

  get isCreative(): boolean {
    return this.entity instanceof Creative;
  }

  get isLineItem(): boolean {
    return this.entity instanceof LineItem;
  }

  get isPlan(): boolean {
    return this.entity instanceof Plan;
  }

  get isPackage(): boolean {
    return this.entity instanceof Package;
  }

  get targetingCriteria(): TargetingCriterion[] {
    if (this.isPublic) {
      return this.state?.orgApp?.ReferenceData?.targetingCriteria || [];
    }
    return peekQueryInScope(useTargetingCriteria);
  }

  get allowExchangeFeeOverride(): boolean {
    return this.state?.orgApp?.organizationSettings?.allowExchangeFeeOverride || false;
  }

  get uiEnableCustomEventManagement(): boolean {
    return this.state?.orgApp?.organizationSettings?.uiEnableCustomEventManagement || false;
  }

  get customEventsSection(): ISection {
    return {
      id: TargetingSection.CustomEvents,
      name: capitalize(i18n.global.t('names.customEvent', { n: 2 })),
      component: markRaw(this.isCreative ? CreativeCustomEvents : CustomEventsSection),
      icon: markRaw(IconEvent),
    };
  }

  get daypartSection(): ISection {
    return {
      id: TargetingSection.Daypart,
      name: capitalize(i18n.global.t('names.daypart', { n: 2 })),
      component: markRaw(this.isCreative ? CreativeDaypart : Daypart),
      icon: markRaw(IconDaypart),
    };
  }

  static get exchangeFeeOverrideSection(): ISection {
    return {
      id: TargetingSection.ExchangeFeeOverride,
      name: capitalize(i18n.global.t('names.exchangeFeeOverride', { n: 1 })),
      component: markRaw(ExchangeFeeOverrideSection),
      icon: markRaw(IconFees),
    };
  }

  get mediaOwnersSection(): ISection {
    return {
      id: TargetingSection.MediaOwners,
      name: capitalize(i18n.global.t('names.mediaOwner', { n: 2 })),
      component: markRaw(this.isLineItem ? LineItemMediaOwners : PlanMediaOwners),
      icon: markRaw(IconMediaOwner),
    };
  }

  get weatherSection(): ISection {
    return {
      id: TargetingSection.Weather,
      name: capitalize(i18n.global.t('messages.weatherTargeting')),
      component: markRaw(this.isCreative ? CreativeWeather : Weather),
      icon: markRaw(IconWeather),
    };
  }

  get pollenSection(): ISection {
    return {
      id: TargetingSection.Pollen,
      name: capitalize(i18n.global.t('messages.pollenTargeting')),
      component: markRaw(this.isCreative ? CreativePollen : PollenSection),
      icon: markRaw(IconPollen),
    };
  }

  static get inventorySection(): ISection {
    return {
      id: TargetingSection.Inventory,
      name: capitalize(i18n.global.t('names.inventory')),
      component: markRaw(Locations),
      icon: markRaw(IconLocation),
    };
  }

  get canTargetWeather(): boolean {
    return (
      TargetingSectionHelper.isNotChina &&
      !this.entity.hasCadencePurchaseType &&
      this.targetingCriteria.filter((x) => this.isWeatherTargetingCriterion(x.type)).length > 0
    );
  }

  get canTargetPollen(): boolean {
    return (
      TargetingSectionHelper.isNotChina &&
      !this.entity?.hasCadencePurchaseType &&
      this.targetingCriteria.filter((x) => x.type === TargetingCriteriaType.Pollen).length > 0
    );
  }

  get demandOrganizationSections(): ISection[] {
    const sections: ISection[] = [this.daypartSection];

    if (!this.isCreative) {
      sections.push(this.mediaOwnersSection);
    }

    if (this.canTargetWeather) {
      sections.push(this.weatherSection);
    }

    if (this.canTargetPollen) {
      sections.push(this.pollenSection);
    }

    if (this.allowExchangeFeeOverride) {
      sections.push(TargetingSectionHelper.exchangeFeeOverrideSection);
    }

    return sections;
  }

  get supplyOrganizationSections(): ISection[] {
    const sections: ISection[] = [this.daypartSection];

    if (!this.entity.isTakeoverEnabled) {
      if (this.canTargetWeather) {
        sections.push(this.weatherSection);
      }

      if (this.canTargetPollen) {
        sections.push(this.pollenSection);
      }
    }

    return sections;
  }

  get allSections(): ISection[] {
    let sections: ISection[] = [];

    if (!this.entity || this.isCuratorOrganization) {
      return sections;
    }

    if (this.entity instanceof Creative) {
      sections.push(TargetingSectionHelper.inventorySection);
    }

    sections = [
      ...sections,
      ...(this.isDemandOrganization ? this.demandOrganizationSections : this.supplyOrganizationSections),
    ];

    if (
      this.uiEnableCustomEventManagement &&
      this.targetingCriteria.filter((x) => x.isUpdatePushedExternally).length > 0 &&
      (this.entity.purchaseType !== PurchaseTypes.CADENCE || this.isPackage)
    ) {
      sections.push(this.customEventsSection);
    }

    return sections;
  }

  get pollenTargeting(): Criterion[] {
    if (this.targetingCriteria?.length === 0 || this.entity.targetingCriteria?.length === 0) {
      return [];
    }

    const pollenTargetCriteria = this.targetingCriteria.filter((x) => x.type === TargetingCriteriaType.Pollen);

    return [...this.entity.targetingCriteria].filter(
      (x) => pollenTargetCriteria.find((y) => y.id === x.targetingCriterionID) != null,
    );
  }

  get targetedCustomEvents(): TargetingCriterionSubType[] {
    if (this.targetingCriteria?.length === 0 || this.entity.targetingCriteria == null) {
      return [];
    }
    const externalPushedTargetCriteria = [...this.targetingCriteria].filter((x) => x.isUpdatePushedExternally);
    const entityTargetingCriteria = [...this.entity.targetingCriteria].filter((x) =>
      externalPushedTargetCriteria.some((ec) => ec.id === x.targetingCriterionID),
    );
    return entityTargetingCriteria
      .map((ctc) =>
        ctc.values.map((x) =>
          externalPushedTargetCriteria
            .find((ec) => ec.id === ctc.targetingCriterionID)
            .subTypes.find((y) => y.code === x.code),
        ),
      )
      .flat()
      .filter((x) => x != null);
  }

  get weatherTargeting(): Criterion[] {
    if (this.targetingCriteria?.length === 0 || this.entity.targetingCriteria?.length === 0) {
      return [];
    }

    const weatherAndTempTargetCriteria = this.targetingCriteria.filter((x) => this.isWeatherTargetingCriterion(x.type));

    return [...this.entity.targetingCriteria].filter(
      (x) => weatherAndTempTargetCriteria.find((y) => y.id === x.targetingCriterionID) != null,
    );
  }

  get weatherTargetingForExport(): { name: string; value: string }[] {
    return this.hasWeatherTargeting
      ? [...this.weatherTargeting]
          .map((x) => x.values)
          .flat()
          .map((x) => this.formatCriterionValueForExport(x))
      : [];
  }

  get hasWeatherTargeting(): boolean {
    return this.weatherTargeting?.length > 0;
  }

  get pollenTargetingForExport(): { name: string; value: string }[] {
    return this.hasPollenTargeting
      ? [...this.pollenTargeting]
          .map((x) => x.values)
          .flat()
          .map((x) => this.formatCriterionValueForExport(x))
      : [];
  }

  get hasPollenTargeting(): boolean {
    return this.pollenTargeting?.length > 0;
  }

  get hasTargetedCustomEvents(): boolean {
    return this.targetedCustomEvents?.length > 0;
  }

  get hasInventoryTargeting(): boolean {
    return this.entity?.hasInventoryTargeting ?? false;
  }

  get selectedSections(): ISection[] {
    const selectedTargetingSections: ISection[] = [];

    if (this.entity.daypart !== FullDaypart) {
      selectedTargetingSections.push(this.daypartSection);
    }

    if (this.hasWeatherTargeting) {
      selectedTargetingSections.push(this.weatherSection);
    }

    if (this.hasPollenTargeting) {
      selectedTargetingSections.push(this.pollenSection);
    }

    if (this.hasTargetedCustomEvents) {
      selectedTargetingSections.push(this.customEventsSection);
    }

    if (this.hasInventoryTargeting) {
      selectedTargetingSections.push(TargetingSectionHelper.inventorySection);
    }

    return selectedTargetingSections;
  }

  isWeatherTargetingCriterion(type: TargetingCriteriaType): boolean {
    return isWeatherCriteria(type);
  }

  updateAllSections(
    entityTargetingCriteria: LineItemTargetingCriterion[],
    availableMediaOwners: MediaOwners[],
  ): ISection[] {
    let selectedAdditionalTargetingCriteria: ISection[] = [];
    const externalPushedTargetCriteria = this.targetingCriteria.filter((x) => x.isUpdatePushedExternally);
    const customEventsTargetCriteria = entityTargetingCriteria.filter((x) =>
      externalPushedTargetCriteria.some((ec) => ec.id === x.targetingCriterionID),
    );
    const weatherTargetingCriteria = this.targetingCriteria.filter((x) => this.isWeatherTargetingCriterion(x.type));
    const entityWeatherTargetCriterion = entityTargetingCriteria.filter((x) =>
      weatherTargetingCriteria.map((c) => c.id).includes(x.targetingCriterionID),
    );
    const pollenTargetingCriterion = this.targetingCriteria.find((x) => x.type === TargetingCriteriaType.Pollen);
    const entityPollenTargetCriterion =
      pollenTargetingCriterion != null
        ? entityTargetingCriteria.filter((x) => pollenTargetingCriterion.id === x.targetingCriterionID)
        : [];

    if (this.isDemandOrganization) {
      selectedAdditionalTargetingCriteria = [
        ...(this.entity.daypart === FullDaypart ? [] : [this.daypartSection]),
        ...(this.entity.mediaOwnerIDs.length === 0 || this.entity.mediaOwnerIDs.length === availableMediaOwners.length
          ? []
          : [this.mediaOwnersSection]),
        ...(entityWeatherTargetCriterion.length === 0 ? [] : [this.weatherSection]),
        ...(entityPollenTargetCriterion.length === 0 ? [] : [this.pollenSection]),
        ...(this.entity instanceof LineItem &&
        (this.entity.exchangeFeeOverrideValue == null || this.entity.exchangeFeeOverrideValue === 0)
          ? []
          : [TargetingSectionHelper.exchangeFeeOverrideSection]),
        ...(customEventsTargetCriteria.length === 0 ? [] : [this.customEventsSection]),
      ];
    } else {
      // Dayparting is not currently supported for curated deals
      selectedAdditionalTargetingCriteria = [
        ...(this.entity.isDaypartEmptyOrFull || this.isCuratorOrganization ? [] : [this.daypartSection]),
        ...(entityWeatherTargetCriterion.length === 0 ? [] : [this.weatherSection]),
        ...(entityPollenTargetCriterion.length === 0 ? [] : [this.pollenSection]),
        ...(customEventsTargetCriteria.length === 0 ? [] : [this.customEventsSection]),
      ];
    }

    return selectedAdditionalTargetingCriteria;
  }

  clearInventory(selectedAdditionalTargetingCriteria: ISection[]): ISection[] {
    return this.clearSection(selectedAdditionalTargetingCriteria, TargetingSection.Inventory);
  }

  clearWeather(selectedAdditionalTargetingCriteria: ISection[]): ISection[] {
    return this.clearSection(selectedAdditionalTargetingCriteria, TargetingSection.Weather);
  }

  clearPollen(selectedAdditionalTargetingCriteria: ISection[]): ISection[] {
    return this.clearSection(selectedAdditionalTargetingCriteria, TargetingSection.Pollen);
  }

  clearUv(selectedAdditionalTargetingCriteria: ISection[]): ISection[] {
    return this.clearSection(selectedAdditionalTargetingCriteria, TargetingSection.Uv);
  }

  clearSection(selectedAdditionalTargetingCriteria: ISection[], targetingSection: TargetingSection): ISection[] {
    return selectedAdditionalTargetingCriteria.filter((x) => x.id !== targetingSection);
  }

  async getAllTargetingCriteria(): Promise<void> {
    await runQueryInScope(useTargetingCriteria);
  }

  newEntityCriteria(entityId: number, criterionId: number, values: { code: string; value: string }[]): Criterion {
    if (this.isLineItem) {
      return new LineItemTargetingCriterion(null, entityId, criterionId, values);
    } else if (this.isPlan) {
      return new PlanTargetingCriterion(null, entityId, criterionId, values);
    } else {
      return new PackageTargetingCriterion(null, entityId, criterionId, values);
    }
  }

  getTemperature(temperatureValue: CriterionValue): string {
    let targetTemperature = `${temperatureValue.value}°C`;
    if (LocalCache.get('temperatureUnit') === 'F') {
      targetTemperature = `${celsiusToFahrenheit(temperatureValue.value)}°F`;
    }
    const fragments = temperatureValue.code.split('_');
    const allButLastFragments = fragments.slice(0, fragments.length - 1);
    const lastFragment = fragments[fragments.length - 1];
    const limitId = allButLastFragments.join('_');
    const limit = WeatherTargetingEnumerations.allTemperatureLimits().find((x) => x.id === limitId);
    const comparator = WeatherTargetingEnumerations.allComparators().find((x) => x.id === lastFragment);
    return `${limit.name} ${comparator.name} ${targetTemperature}`;
  }

  formatCriterionValueForExport(criterionValue: CriterionValue): { name: string; value: string } {
    const values = criterionValue.value.split(',');
    switch (criterionValue.code) {
      case TargetingCriteriaType.Weather:
        return {
          name: capitalize(i18n.global.t('messages.targetWeatherConditions')),
          value: values.map((v) => allWeathers().find((w) => w.id === v)?.name ?? v).join(', '),
        };
      case TargetingCriteriaType.DailyMaxAbove:
        return {
          name: capitalize(i18n.global.t('messages.targetTemperature')),
          value: this.getTemperature(criterionValue),
        };
      case TargetingCriteriaType.UvCategory:
        return {
          name: capitalize(i18n.global.t('messages.uvTargeting')),
          value: values.map((v) => allUVs().find((u) => u.id === v).name).join(', '),
        };
      case TargetingCode.PollenCategory:
        return {
          name: capitalize(i18n.global.t('messages.pollenTargeting')),
          value: values.map((v) => allPollens().find((p) => p.id === v)?.name ?? v).join(', '),
        };
      default:
        return {
          name: criterionValue.code,
          value: criterionValue.value,
        };
    }
  }
}
